import { useEffect, useState } from "react";
import Button from "../../../shared/components/Button";
import { Box } from "../../../shared/components/Box";
import { Container } from "../../../shared/components/Container";
import { Grid } from "../../../shared/components/Grid";
import { Layout } from "../../../layouts/Layout";
import { loadWorkflowFeedCameras } from "../../workflow/api";
import { cameraStreamURL, loadCameras, removeCamera as deleteCamera } from "../api";
import { CameraCard } from "../components/CameraCard";
import { CameraFocusModal } from "../components/CameraFocusModal";
import { CameraModal } from "../components/CameraModal";
import { CameraSettingsModal } from "../components/CameraSettingsModal";
import { CameraToolbar, type GridColumns, type StatusFilter } from "../components/CameraToolbar";
import type { Camera } from "../model";
import "../pages/CamerasPage.scss";

const cameraLayoutStorageKey = "owl:cameras:layout";

function mapCamera(item: Record<string, string>): Camera {
  return {
    id: item.id,
    location: item.location,
    status: (item.status as Camera["status"]) ?? "Offline",
    lastSeen: item.last_seen ?? "Never",
    protocol: item.protocol,
    accent: "#3a3b3e",
    streamURL: cameraStreamURL(item.id),
  };
}

function mapWorkflowCamera(camera: ReturnType<typeof loadWorkflowFeedCameras>[number]): Camera {
  return {
    id: camera.id,
    location: camera.location,
    status: "Offline",
    lastSeen: "Never",
    protocol: camera.protocol,
    accent: "#3a3b3e",
    streamURL: camera.streamURL,
  };
}

function readGridColumns(): GridColumns {
  try {
    const saved = Number(window.localStorage.getItem(cameraLayoutStorageKey));
    return saved === 3 || saved === 4 ? saved : 2;
  } catch {
    return 2;
  }
}

export function CamerasDesktopView() {
  const [cameraList, setCameraList] = useState<Camera[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [settingsCamera, setSettingsCamera] = useState<Camera | null>(null);
  const [expandedCamera, setExpandedCamera] = useState<Camera | null>(null);
  const [gridColumns, setGridColumns] = useState<GridColumns>(readGridColumns);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("All");
  const [protocolFilter, setProtocolFilter] = useState("All");

  useEffect(() => {
    try {
      window.localStorage.setItem(cameraLayoutStorageKey, String(gridColumns));
    } catch {
      // Layout preference is optional.
    }
  }, [gridColumns]);

  useEffect(() => {
    const feedCameras = loadWorkflowFeedCameras();
    const feedByID = new Map(feedCameras.map((camera) => [camera.id, camera]));
    loadCameras()
      .then((payload) => {
        const backendCameras = payload.filter((camera) => feedByID.has(camera.id)).map(mapCamera);
        const backendIDs = new Set(backendCameras.map((camera) => camera.id));
        setCameraList([...backendCameras, ...feedCameras.filter((camera) => !backendIDs.has(camera.id)).map(mapWorkflowCamera)]);
      })
      .catch(() => setCameraList(feedCameras.map(mapWorkflowCamera)));
  }, []);

  const removeCamera = async (camera: Camera) => {
    if (!window.confirm(`Remove ${camera.location}?`)) return;
    try {
      await deleteCamera(camera.id);
      setCameraList((current) => current.filter((item) => item.id !== camera.id));
    } catch {
      // Keep camera visible when backend deletion fails.
    }
  };
  const protocols = Array.from(new Set(cameraList.map((camera) => camera.protocol).filter((protocol): protocol is string => Boolean(protocol)))).sort();
  const normalizedSearch = search.trim().toLowerCase();
  const visibleCameras = cameraList.filter((camera) =>
    [camera.id, camera.location, camera.protocol].filter(Boolean).some((value) => value!.toLowerCase().includes(normalizedSearch)) &&
    (statusFilter === "All" || camera.status === statusFilter) &&
    (protocolFilter === "All" || camera.protocol === protocolFilter),
  );
  const hasFilters = search !== "" || statusFilter !== "All" || protocolFilter !== "All";
  const clearFilters = () => {
    setSearch("");
    setStatusFilter("All");
    setProtocolFilter("All");
  };

  return (
    <Layout activePath="/cameras" className="cameras-page">
      <Box as="main" className="main">
        <header className="header">
          <div><p className="eyebrow">Camera monitoring</p><h1>Cameras</h1></div>
          <div className="header-actions">
            <span className="online-count">{cameraList.filter((camera) => camera.status === "Live").length} online</span>
            <Button onClick={() => setShowAdd(true)} variant="primary">+ Add camera</Button>
          </div>
        </header>
        <Container className="content" fluid>
          <div className="page-heading">
            <h2>Camera feeds</h2>
            <p>Live status across monitored locations · {visibleCameras.length} of {cameraList.length} shown</p>
          </div>
          <CameraToolbar
            gridColumns={gridColumns} hasFilters={hasFilters} protocolFilter={protocolFilter} protocols={protocols}
            search={search} statusFilter={statusFilter} onClearFilters={clearFilters} onGridColumnsChange={setGridColumns}
            onProtocolFilterChange={setProtocolFilter} onSearchChange={setSearch} onStatusFilterChange={setStatusFilter}
          />
          {cameraList.length === 0 ? (
            <div className="empty-state"><b>No cameras configured</b><span>Add an output feed node in Workflow to expose a camera.</span></div>
          ) : visibleCameras.length === 0 ? (
            <div className="empty-state"><b>No matching cameras</b><span>Try another search or filter.</span><Button onClick={clearFilters} variant="secondary">Clear filters</Button></div>
          ) : (
            <Grid className={`camera-grid camera-grid--${gridColumns}`}>
              {visibleCameras.map((camera) => <CameraCard camera={camera} key={camera.id} onExpand={setExpandedCamera} onRemove={removeCamera} onSettings={setSettingsCamera} />)}
            </Grid>
          )}
        </Container>
      </Box>
      {showAdd && <CameraModal mapCamera={mapCamera} onClose={() => setShowAdd(false)} onCreated={(camera) => setCameraList((current) => [...current, camera])} />}
      {settingsCamera && <CameraSettingsModal camera={settingsCamera} onClose={() => setSettingsCamera(null)} />}
      {expandedCamera && <CameraFocusModal camera={expandedCamera} onClose={() => setExpandedCamera(null)} />}
    </Layout>
  );
}
